import React, { useMemo, useState, useRef, useEffect } from "react";
import { fmtNum, positionColor } from "@/lib/format";

export function PlayerSearch({
  players,
  onPick,
  placeholder = "Search players or picks…",
  testId = "player-search",
  showDisableRosterFilter = false,
  onDisableRosterFilter,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return players
      .filter(p => p.name.toLowerCase().includes(term))
      .slice(0, 12);
  }, [q, players]);

  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={wrapRef} data-testid={`${testId}-wrap`}>
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full bg-white border border-[#0A0A0A]/30 focus:border-[#0A0A0A] outline-none px-3 py-2.5 text-sm placeholder:text-[#52525B]/70 transition-colors font-mono-data"
        data-testid={`${testId}-input`}
      />
      {open && q.trim() && (results.length > 0 || showDisableRosterFilter) && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#0A0A0A] max-h-80 overflow-auto shadow-[4px_4px_0_0_#0A0A0A]" data-testid={`${testId}-results`}>
          {showDisableRosterFilter && (
            <button
              onClick={() => {
                onDisableRosterFilter?.();
                setOpen(true);
              }}
              className="sticky top-0 z-10 w-full px-3 py-2 text-left text-[10px] uppercase tracking-widest font-mono-data bg-[#FACC15] hover:bg-[#0A0A0A] hover:text-white border-b border-[#0A0A0A] transition-colors"
              data-testid={`${testId}-disable-roster-filter`}
            >
              Disable Roster Filter
            </button>
          )}
          {results.map((p, index) => {
            const resultKey = p.tradeItemKey || p.rosterInstanceKey || p.finderKey || `${p.id}-${index}`;
            return (
            <button
              key={resultKey}
              onClick={() => { onPick(p); setQ(""); setOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#0A0A0A] hover:text-white border-b border-[#E4E4E7] last:border-b-0 transition-colors"
              data-testid={`${testId}-result-${resultKey}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 ${positionColor(p.position)} group-hover:bg-white`}>{p.position}</span>
                <span className="truncate text-sm font-medium">{p.name}</span>
                {p.team && <span className="text-xs text-[#52525B] font-mono-data">{p.team}</span>}
              </div>
              <span className="font-mono-data text-sm tabular-nums">{fmtNum(p.combinedValue)}</span>
            </button>
            );
          })}
          {results.length === 0 && (
            <div className="px-3 py-3 text-xs text-[#52525B] font-mono-data border-b border-[#E4E4E7]">
              No roster matches.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
